import { withDynamicOptionsAdapterHOC, type WithDynamicOptionsHOCProps } from "@client/hocs/withDynamicOptions";
import { getGoogleFontLabel, isGoogleFont } from "@client/lib/fonts";
import systemFonts from "@client/lib/systemFonts.json";
import { type OptionItem } from "@client/types";
import AutoComplete, {
  type AutoCompleteProps,
  DynamicAutoComplete as DynamicAutoCompleteCommon
} from "@common/controls/Inputs/Select/AutoComplete/component";
import apiV1 from "@common/lib/api";
import { sort } from "@common/lib/array";
import { EMPTY_ARRAY } from "@common/lib/consts";
import { usePromise } from "@common/lib/hooks";
import React from "react";

export default AutoComplete;

export type DynamicAutoCompleteProps = AutoCompleteProps & WithDynamicOptionsHOCProps<OptionItem>;
export const DynamicAutoComplete = withDynamicOptionsAdapterHOC<AutoCompleteProps, OptionItem>(
  DynamicAutoCompleteCommon
);

export type FontsAutoCompleteProps = Omit<AutoCompleteProps, "options" | "noMatchLabel">;

export const FontsAutoComplete = (props: FontsAutoCompleteProps) => {
  const [fonts] = usePromise({
    fn: async () => {
      const googleFonts = await apiV1.get<{ caption: string; value: string }[]>(
        {},
        "/integration/google/fonts",
        undefined,
        "memory"
      );
      return sort([...systemFonts, ...(googleFonts ?? []).map((x) => ({ label: x.caption, value: x.value }))], {
        key: "label"
      });
    },
    args: [],
    shouldLoad: true
  });

  // NOTE: we had used hard-coded google fonts, then we started pulling them from the google API.
  // Some font values didn't match changed, thus we use the label to find the selected value.
  const value = React.useMemo(() => {
    if (!fonts || !props.value || !isGoogleFont(props.value)) return props.value;

    const v = fonts.find((x) => x.value === props.value);
    if (v) return props.value;

    const label = getGoogleFontLabel(props.value);
    return fonts.find((x) => x.label === label)?.value;
  }, [props.value, fonts]);

  return <AutoComplete {...props} options={fonts ?? EMPTY_ARRAY} noMatchLabel="No matching fonts" value={value} />;
};

export {
  AutoCompleteLazy,
  AutoCompleteLazyWithAdd,
  AutoCompleteWithAdd,
  DynamicAutoCompleteWithAdd,
  type AutoCompleteContentProps,
  type AutoCompleteLazyProps,
  type AutoCompleteProps,
  type GetOptions
} from "@common/controls/Inputs/Select/AutoComplete/component";
